// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.RightPanelContent.js" company="Izenda">  Copyright (c) 2015
// Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.  </copyright>
// ----------------------------------------------------------------------

import React from 'react';

import connect from 'share/enhancers/Izenda.Connect';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Input from 'share/components/controls/Izenda.Input';
import classnames from 'classnames';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import constants from 'logic/data/Izenda.PermissionConstants';
import accessConstants from 'logic/data/Izenda.AccessConstants';
import AccessDefaultGrid from './Izenda.AccessDefaultGrid';
import { map, isObject, find, isNil, filter, split, isEmpty, get, uniqueId, forEach, join, take } from 'lodash/fp';
const RightPanelContent = connect()(props => {
  const renderLabelColumn = data => {
    const localization = props.localization;
    let listLabel = [];

    // Data for popup
    let popupData;

    // Padding for inner item
    const addToRenderList = (item, shouldPadding) => {
      let value = isObject(item) ? item.name : item;
      const className = classnames('u_textTruncate', { 'tree-data-inner': shouldPadding });
      listLabel.push(<div className={className}>{value}</div>);
    };

    if (props.treeType === constants.TREE_TYPE.CATEGORY) {
      const globalCategories = find(item => item.isGlobal, data);
      const localCategories = find(item => !item.isGlobal, data);
      const savableCategories = props.savableCategories;

      const renderCategory = (category, shouldPadding) => {
        if (!isNil(category)) {
          !savableCategories && listLabel.push(<div className="u_textTruncate">{category.name}</div>);
          // Maximum 3 line
          // If global or local category label is render, only take 2 items from category, otherwise take 3
          const itemsToTake = savableCategories
            ? constants.NUMBER_CATEGORY_TAKE.SAVEABLE
            : constants.NUMBER_CATEGORY_TAKE.VISIBLE;
          const subCategories = take(
            itemsToTake,
            filter(item => !savableCategories || item.savable, category.subCategories)
          );

          subCategories.forEach(item => {
            addToRenderList(item, shouldPadding);
          });
        }
      };
      !savableCategories && renderCategory(globalCategories, true);
      renderCategory(localCategories, !savableCategories);

      popupData = savableCategories ? filter(item => !item.isGlobal, data) : data;
    } else if (props.treeType === constants.TREE_TYPE.ACCESS) {
      take(3, data).forEach((item, index) => {
        if (props.isAccessDefault) {
          const sharingSelection =
            item.assignedType === accessConstants.ASSIGN_TYPE.ALL
              ? localization.getText('ACCESS_CONSTANT_LABEL_ASSIGNED_TYPE_EVERYONE')
              : join(', ', item.accessorNames.sort());
          const value = `${index + 1} ${sharingSelection}`;

          addToRenderList(value, false);
        } else {
          addToRenderList(item, false);
        }
      });
      popupData = data;
    }

    // Add more button if and only if the cell is not empty
    if (!isEmpty(listLabel)) {
      const onClick = () => {
        props.openPopup(MoreInfoPopup, {
          isAccessDefault: props.isAccessDefault,
          popupData,
          treeType: props.treeType,
          checkSavable: props.savableCategories,
          popupTitle: props.popupTitle,
          popupMessage: props.popupMessage
        });
      };

      listLabel.push(
        <a className="u-colorBlue u_textUnderline u_floatRight" onClick={onClick}>
          {localization.getText('ROLE_SETUP_PERMISSION_SUMMARY_MORE')}
        </a>
      );
    }
    return listLabel;
  };

  const renderCheckboxColumn = () => {
    const columns = [];
    const { model } = props;

    model &&
      !isEmpty(model.permissionSummary) &&
      forEach(item => {
        var value = get(props.keysPath, item.permission);

        columns.push(
          <GridLayout.Col
            cols={1}
            className={classnames('grid-small-column u_borderRight', {
              'grid-medium-column': props.notShowBlankCharacter && isEmpty(value),
              'grid-medium-column-has-content': props.showRole && !isEmpty(value)
            })}
            key={uniqueId(item.id)}
          >
            {!props.isParent && <Input type="checkbox" checked={value} disabled />}
            {props.isParent && (!props.notShowBlankCharacter || isEmpty(value)) && <span>&nbsp;</span>}
            {props.showRole && !isEmpty(value) && renderLabelColumn(value)}
            {props.showRadioButton && <Input type="radio" checked={value} disabled />}
          </GridLayout.Col>
        );
      }, take(12, model.permissionSummary));
    return columns;
  };

  return (
    <GridLayout.Col cols="9" className="u_noPaddingAll text-center">
      <GridLayout.Row className="u_noMarginAll">
        <GridLayout.Col cols="12" className="u_noPaddingAll">
          {renderCheckboxColumn()}
        </GridLayout.Col>
      </GridLayout.Row>
    </GridLayout.Col>
  );
});

const MoreInfoPopup = connect()(props => {
  const { localization, checkSavable, popupData, treeType } = props;
  // Property to render and access path to child nodes
  let childPath, namePath, altNamePath;

  const isCategoryType = treeType === constants.TREE_TYPE.CATEGORY;
  if (isCategoryType) {
    childPath = 'subCategories';
    namePath = 'name';
  } else {
    childPath = 'users';
    namePath = 'fullName';
    altNamePath = 'name';
  }

  const renderAccessDefault = data => {
    return <AccessDefaultGrid data={data} />;
  };

  // Recursive render category/role
  const renderTree = (items, depth) => {
    // Remove most outer item when view saving category (don't show 'Local Categories' label)
    const removeMostOuterItem = !!(checkSavable && depth === 0);
    const style = {
      paddingLeft: `${depth * 10}px`
    };
    return (
      <div>
        {map(item => {
          return (
            !(
              (isCategoryType && checkSavable && !item.savable) ||
              (!isCategoryType && depth !== 0 && item.notAllowSharing)
            ) && (
              <div style={style}>
                {!removeMostOuterItem && (
                  <GridLayout.Row key={item.id}>
                    {isNil(item[namePath]) ? item[altNamePath] : item[namePath]}
                  </GridLayout.Row>
                )}
                {item[childPath] && renderTree(item[childPath], depth + 1)}
              </div>
            )
          );
        }, items)}
      </div>
    );
  };

  const popupMessages = split('\n', props.popupMessage);

  return (
    <Popup visible type="none" onClosing={props.onReject} autoClose={false} sizeClass="modal-lg">
      <Popup.Header title={props.popupTitle} className="u_floatLeft" />
      <Popup.Body>
        <GridLayout className="izenda-PasswordOptions">
          {map(message => {
            return <GridLayout.Row>{message}</GridLayout.Row>;
          }, popupMessages)}
          {props.isAccessDefault ? renderAccessDefault(popupData) : renderTree(popupData, 0)}
        </GridLayout>
      </Popup.Body>
      <Popup.Footer>
        <Popup.ConfirmationButton type="cancel">{localization.getText('COMMON_LABEL_CLOSE')}</Popup.ConfirmationButton>
      </Popup.Footer>
    </Popup>
  );
});

export default RightPanelContent;



// WEBPACK FOOTER //
// ./ui/modules/Settings/RoleSetup/RoleDetail/PermissionSummary/Izenda.RightPanelContent.js